import React, { useEffect, useMemo, useState } from "react";
import {
  Chip,
  Group,
  Box,
  SegmentedControl,
  Stack,
  useMantineTheme,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import moment from "moment";
import _ from "lodash";
import BarChart from "./chartWidget/BarChart";
import SimpleLoadingIndicator from "./simpleLoadingIndicator";

const MONTH_LABELS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const numFormatter = Intl.NumberFormat("en", { notation: "compact" });

const formatCompactCurrency = (value) => {
  if (value == null || Number.isNaN(Number(value))) return "0";
  return `${numFormatter.format(Number(value))}`;
};

const YEAR_COMPARISON_YEAR_COUNT = 5;

const getDefaultComparisonYears = (currentYear) =>
  Array.from(
    { length: YEAR_COMPARISON_YEAR_COUNT },
    (_, index) => currentYear - (YEAR_COMPARISON_YEAR_COUNT - 1 - index)
  );

const YEAR_COLORS = [
  "#5f3dc4",
  "#7b2cbf",
  "#1864ab",
  "#228be6",
  "#74c0fc",
];

const buildChartData = (yearComparison, selectedYears, metric) => {
  const { data = [] } = yearComparison || {};
  const currentYear = moment().year();
  const currentMonth = moment().month() + 1;

  return MONTH_LABELS.map((label, index) => {
    const monthNum = index + 1;
    const row = { month: label };

    selectedYears.forEach((year) => {
      if (year === currentYear && monthNum > currentMonth) return;
      const yearEntry = data.find((entry) => entry.year === year);
      const monthEntry = yearEntry?.months?.find((m) => m.month === monthNum);
      row[String(year)] = monthEntry?.[metric] ?? 0;
    });

    return row;
  });
};

const DashboardYearComparisonTab = ({ yearComparison = {}, loading = false }) => {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const currentYear = moment().year();

  const availableYears = useMemo(() => {
    const years = !_.isEmpty(yearComparison?.years)
      ? yearComparison.years
      : getDefaultComparisonYears(currentYear);
    return [...years].sort((a, b) => a - b);
  }, [yearComparison?.years, currentYear]);

  const [selectedYears, setSelectedYears] = useState([
    currentYear,
    currentYear - 1,
  ]);
  const [metric, setMetric] = useState("grossPremium");

  useEffect(() => {
    setSelectedYears([currentYear, currentYear - 1]);
  }, [currentYear]);

  const sortedSelectedYears = useMemo(
    () => [...selectedYears].sort((a, b) => a - b),
    [selectedYears]
  );

  const chartData = useMemo(
    () => buildChartData(yearComparison, sortedSelectedYears, metric),
    [yearComparison, sortedSelectedYears, metric]
  );

  const yearKeys = useMemo(
    () => sortedSelectedYears.map(String),
    [sortedSelectedYears]
  );

  const yearColors = useMemo(() => {
    const colorMap = {};
    availableYears.forEach((year, index) => {
      colorMap[String(year)] =
        YEAR_COLORS[index % YEAR_COLORS.length] ||
        theme.colors.cyan?.[6] ||
        "#228be6";
    });
    return (bar) => colorMap[bar.id] || theme.colors.blue?.[6];
  }, [availableYears, theme.colors]);

  const chartTitle = useMemo(() => {
    const yearsLabel = sortedSelectedYears.join(" & ");
    const metricLabel =
      metric === "grossPremium"
        ? t("dashboard.yearComparison.grossPremium", "Gross Premium")
        : t("dashboard.yearComparison.brokerage", "Brokerage");
    return t("dashboard.yearComparison.title", "{{years}} {{metric}} Comparison", {
      years: yearsLabel,
      metric: metricLabel,
    });
  }, [sortedSelectedYears, metric, t]);

  const handleYearChange = (values) => {
    if (!values?.length) return;
    setSelectedYears(values.map(Number).sort((a, b) => a - b));
  };

  return (
    <Box pos="relative" mt="50px">
      <SimpleLoadingIndicator visible={loading} />
      <Stack spacing="md">
      <Group position="apart" align="flex-end">
        <Chip.Group
          multiple
          value={selectedYears.map(String)}
          onChange={handleYearChange}
        >
          <Group spacing="xs">
            {availableYears.map((year) => (
              <Chip key={year} value={String(year)} size="sm">
                {year}
              </Chip>
            ))}
          </Group>
        </Chip.Group>

        <SegmentedControl
          value={metric}
          onChange={setMetric}
          data={[
            {
              label: t(
                "dashboard.yearComparison.grossPremium",
                "Gross Premium"
              ),
              value: "grossPremium",
            },
            {
              label: t("dashboard.yearComparison.brokerage", "Brokerage"),
              value: "brokerage",
            },
          ]}
        />
      </Group>

      <BarChart
        title={chartTitle}
        height={480}
        data={chartData}
        useSampleData={false}
        chartProps={{
          keys: yearKeys,
          indexBy: "month",
          layout: "vertical",
          groupMode: "grouped",
          colors: yearColors,
          enableLabel: true,
          labelSkipWidth: 12,
          labelSkipHeight: 12,
          valueFormat: (value) => formatCompactCurrency(value),
          margin: { top: 20, right: 40, bottom: 100, left: 70 },
          axisLeft: {
            format: (value) => formatCompactCurrency(value),
          },
          axisBottom: {
            tickRotation: 0,
          },
          legends: [
            {
              dataFrom: "keys",
              anchor: "bottom",
              direction: "row",
              justify: false,
              translateX: 0,
              translateY: 72,
              itemsSpacing: 24,
              itemDirection: "left-to-right",
              itemWidth: 80,
              itemHeight: 20,
              symbolSize: 10,
            },
          ],
        }}
      />
    </Stack>
    </Box>
  );
};

export default DashboardYearComparisonTab;
